import React, { useRef, useState, useEffect } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { animated } from '@react-spring/web';
import { rgb } from 'd3-color';

/** ===========================
 * Helpers
 * ============================
 */

// Determine if a color is dark or light for text contrast
const isColorDark = (color) => {
  const { r, g, b } = rgb(color);
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
  return brightness < 128;
};

// Sanitize IDs for gradients
const safeId = (id) => id.replace(/\s+/g, '');

// Default style for any center text
const defaultCenterTextStyle = {
  fontSize: '18px',
  fontWeight: '400',
  fontFamily: 'var(--font-main)',
  fill: '#333333',
  textAnchor: 'middle',
  dominantBaseline: 'middle',
};

/**
 * Wrap text to fit in center
 * (optional feature if you have longer center text)
 */
const wrapText = (text, maxWidth, fontSize) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = getTextWidth(
      `${currentLine} ${word}`,
      `${fontSize} ${defaultCenterTextStyle.fontFamily}`
    );
    if (width < maxWidth) {
      currentLine += ` ${word}`;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

const getTextWidth = (text, font) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

/** ===========================
 * NivoDonut Component
 * ============================
 */
const NivoDonut = ({
  data,
  centerText,
  centerTextStyle,
  showLegend = true,
  showLegendValues = false,
  showLabels = false,
  radius = 0.7,
}) => {
  const [legendTranslateX, setLegendTranslateX] = useState(160);
  const chartContainerRef = useRef(null);

  const mergedCenterTextStyle = {
    ...defaultCenterTextStyle,
    ...centerTextStyle,
  };

  // Dynamically adjust legend position based on container width
  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        setLegendTranslateX(containerWidth > 500 ? 160 : 100);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sort in descending order of value; limit to 8 items
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const visibleData = sortedData.slice(0, 8);
  const hiddenDataCount = sortedData.length - visibleData.length;

  // Build legend items; add a "+X more" if we have more than 8
  const legendItems = visibleData.map(item => ({
    id: item.id,
    label: item.id,
    value: item.value,
    color: item.color,
  }));

  if (hiddenDataCount > 0) {
    legendItems.push({
      id: 'more-items',
      label: `+${hiddenDataCount} more`,
      value: null,
      color: '#ccc',
    });
  }

  const basePadding = showLabels ? 70 : 20;

  return (
    <div ref={chartContainerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        {/* Drop shadow definition (optional) */}
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="10" floodColor="#6b6b6b" floodOpacity="0.5" />
          </filter>
        </defs>
      </svg>

      <ResponsivePie
        data={visibleData}
        margin={{
          top: basePadding,
          right: showLegend ? 160 : basePadding,
          bottom: basePadding,
          left: basePadding,
        }}
        innerRadius={radius}
        padAngle={1}
        cornerRadius={3}
        activeOuterRadiusOffset={8}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['brighter', 1]] }}
        arcLinkLabelsSkipAngle={10}
        enableArcLinkLabels={showLabels}
        arcLinkLabelsThickness={1}
        arcLinkLabelsColor={{ from: 'color' }}
        arcLinkLabelsTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
        arcLabelsSkipAngle={10}
        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
        arcLinkLabelsOffset={8}
        arcLinkLabelsDiagonalLength={0}
        arcLinkLabelsStraightLength={0}
        colors={({ data }) => data.color}

        /**
         * 1) Radial Gradients for a subtle, "shaded" look
         */
        defs={visibleData.map(item => ({
          id: `gradient-${safeId(item.id)}`,
          type: 'radialGradient',
          colors: [
            { offset: 0, color: item.color, opacity: 0.5 },
            { offset: 50, color: item.color, opacity: 0.8 },
            { offset: 100, color: item.color, opacity: 1 },
          ],
        }))}
        fill={visibleData.map(item => ({
          match: { id: safeId(item.id) },
          id: `gradient-${safeId(item.id)}`,
        }))}

        /**
         * 2) Simplified arc labels—just the numeric value
         *    with a small text shadow for readability
         */
        arcLabelsComponent={({ datum, label, style }) => (
          <animated.g transform={style.transform} style={{ pointerEvents: 'none' }}>
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fill={isColorDark(datum.color) ? '#ffffff' : '#000000'}
              style={{
                fontSize: 10,
                fontWeight: 500,
                textShadow: '0 0 3px rgba(0,0,0,0.35)',
              }}
            >
              {parseFloat(label).toFixed(1)}
            </text>
          </animated.g>
        )}

        theme={{
          labels: {
            text: {
              fontSize: 12,
              fontWeight: 500,
              fontFamily: 'var(--font-main)',
            },
          },
          legends: {
            text: { fontSize: 12 },
          },
        }}
        legends={
          showLegend
            ? [
                {
                  anchor: 'top-right',
                  direction: 'column',
                  translateX: legendTranslateX,
                  translateY: 10,
                  itemsSpacing: 10,
                  itemWidth: 120,
                  itemHeight: 25,
                  itemTextColor: '#000',
                  itemDirection: 'left-to-right',
                  itemOpacity: 1,
                  symbolSize: 14,
                  symbolShape: 'circle',
                  itemFormatter: (item) => {
                    const legendItem = legendItems.find((d) => d.id === item);
                    if (showLegendValues && legendItem?.value !== null) {
                      return `${legendItem.label} (${legendItem.value})`;
                    }
                    return legendItem?.label || item;
                  },
                },
              ]
            : []
        }
        layers={[
          'arcs',
          'arcLabels',
          'arcLinkLabels',
          'legends',
          /**
           * 3) Center text (if provided), wrapped to fit
           */
          ({ centerX, centerY, innerRadius }) => {
            if (!centerText) return null;
            const lines = wrapText(centerText, innerRadius * 1.5, mergedCenterTextStyle.fontSize);
            return (
              <g>
                {lines.map((line, i) => (
                  <text
                    key={i}
                    x={centerX}
                    y={
                      centerY +
                      (i - (lines.length - 1) / 2) *
                        parseInt(mergedCenterTextStyle.fontSize, 10)
                    }
                    textAnchor="middle"
                    style={{
                      ...mergedCenterTextStyle,
                      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    {line.toUpperCase()}
                  </text>
                ))}
              </g>
            );
          },
        ]}
      />
    </div>
  );
};

export default NivoDonut;
